import { Form, InputNumber, InputNumberProps, Spin, Typography } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { Rule } from 'antd/es/form';
import InfoTooltip from 'components/info/info';
import Riyal from 'components/riyal/riyal';
import { isRTL } from 'core/services/language';
import React from 'react';

interface FormItemInputProps {
	name: string | (string | number)[];
	label?: string;
	rules?: Rule[];
	fullWidth?: boolean;
	disabled?: boolean;
	description?: string;
	min?: number;
	max?: number;
	step?: number;
	addonAfter?: React.ReactNode;
	simple?: boolean;
	value?: number;
	formClasses?: string;
	inputClasses?: string;
	formStyles?: React.CSSProperties;
	inputStyles?: React.CSSProperties;
	size?: SizeType;
	showPrice?: boolean;
	precision?: number;
	onChange?: (value: number | null) => void;
	loading?: boolean;
	noAutoComplete?: boolean;
	zeroOnBlur?: boolean; // when the input is left empty, set it to 0 on blur
}

interface NumberInputControlProps extends Omit<InputNumberProps, 'onChange' | 'precision'> {
	onChange?: (value: number | null) => void; // injected by Form.Item, updates the form store
	onValueChange?: (value: number | null) => void; // consumer callback (rounded value)
	zeroOnBlur?: boolean;
	precision: number;
}

// Add thousands separators to the integer part only (1000.5 -> 1,000.5)
export const formatWithCommas = (value: string | number | undefined) => {
	if (value === undefined || value === null || value === '') return '';
	const [integerPart, decimalPart] = `${value}`.split('.');
	const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

export const parseWithoutCommas =(value: string | undefined) => (value ? value.replace(/,/g, '') : '');

// Separate control so we can access the value/onChange that Form.Item injects
// (needed for zeroOnBlur, since the form path is unknown inside Form.List)
const NumberInputControl = ({ value, onChange, onValueChange, zeroOnBlur, precision, ...inputProps }: NumberInputControlProps) => {
	const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		// Allow: numbers, decimal point, backspace, delete, arrow keys, tab
		const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
		const key = e.key;

		// Allow control keys
		if (allowedKeys.includes(key)) {
			return;
		}

		// Allow Ctrl/Cmd + A, C, V, X
		if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(key.toLowerCase())) {
			return;
		}

		// Allow numbers
		if (/^\d$/.test(key)) {
			// Check if adding this digit would exceed 2 decimal places
			const input = e.currentTarget;
			const cursorPosition = input.selectionStart || 0;
			const currentValue = input.value;
			const decimalIndex = currentValue.indexOf('.');

			// If there's a decimal point and cursor is after it
			if (decimalIndex !== -1 && cursorPosition > decimalIndex) {
				const decimalPart = currentValue.substring(decimalIndex + 1);
				// If already 2 digits after decimal, prevent input
				if (decimalPart.length >= precision) {
					e.preventDefault();
					return;
				}
			}
			return;
		}

		// Allow decimal point (only one)
		if ((key === '.' || key === '٫') && precision > 0) {
			const input = e.currentTarget;
			const currentValue = input.value;
			// Prevent multiple decimal points
			if (currentValue.includes('.') || currentValue.includes('٫')) {
				e.preventDefault();
				return;
			}
			return;
		}

		// Block all other keys
		e.preventDefault();
	};

	const handleChange = (newValue: number | null) => {
		// Round to 2 decimal places
		if (newValue !== null) {
			const rounded = Math.round(newValue * 100) / 100;
			onValueChange?.(rounded);
		} else {
			onValueChange?.(newValue);
		}
	};

	const handleBlur = () => {
		if (zeroOnBlur && (value === undefined || value === null || value === '')) {
			onChange?.(0);
			onValueChange?.(0);
		}
	};

	return (
		<InputNumber
			{...inputProps}
			value={value}
			precision={precision}
			formatter={formatWithCommas}
			parser={parseWithoutCommas}
			onChange={(newValue) => {
				onChange?.(newValue as number | null);
				handleChange(newValue as number | null);
			}}
			onKeyDown={handleKeyPress}
			onBlur={handleBlur}
		/>
	);
};

const FormNumberElement = ({
	name,
	description,
	disabled,
	fullWidth,
	label,
	rules,
	min,
	max,
	step,
	addonAfter,
	simple,
	value,
	formClasses,
	inputClasses,
	formStyles,
	inputStyles,
	size,
	showPrice,
	precision = 0,
	loading = false,
	onChange,
	zeroOnBlur,
	noAutoComplete,
}: FormItemInputProps) => {
	return (
		<div>
			{label && (
				<Typography.Text style={{ marginBottom: 5, display: 'block', fontSize: 15, textAlign: 'start' }}>
					{label}
					{description && <InfoTooltip title={description} style={{ marginTop: -2 }} />}
				</Typography.Text>
			)}

			<Spin spinning={loading}>
				<Form.Item
					name={name}
					rules={rules}
					className={`${formClasses}`}
					help={null}
					style={{ maxWidth: fullWidth ? 'unset' : 'var(--inputWidth)', ...formStyles }}>
					<NumberInputControl
						style={{ width: '100%', ...inputStyles }}
						className={`${simple ? 'simple' : ''} ${inputClasses}`}
						min={min ?? 0}
						max={max}
						step={step ?? 0.01}
						size={size}
						disabled={disabled}
						precision={precision}
						addonBefore={showPrice && !isRTL() && <Riyal />}
						addonAfter={showPrice && isRTL() ? <Riyal /> : addonAfter ?? null}
						value={value}
						onValueChange={onChange}
						zeroOnBlur={zeroOnBlur}
						autoComplete={noAutoComplete ? 'new-password' : undefined}
					/>
				</Form.Item>
			</Spin>
		</div>
	);
};

export default FormNumberElement;
